import { useEffect, useState } from 'react';
import {
  fetchSavingsPerKiloHistoryLogs,
  fetchSavingsPerKiloSetting,
  updateSavingsPerKiloSetting,
} from '../services/masterSettingService';

function useSavingsPerKiloSetting() {
  const [savingsPerKilo, setSavingsPerKilo] = useState('2');
  const [formError, setFormError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoadError, setHistoryLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  useEffect(() => {
    let isMounted = true;

    async function loadSavingsPerKilo() {
      setIsLoading(true);
      setIsHistoryLoading(true);
      setLoadError('');
      setHistoryLoadError('');

      try {
        const [settingResponse, historyResponse] = await Promise.all([
          fetchSavingsPerKiloSetting(),
          fetchSavingsPerKiloHistoryLogs(),
        ]);

        if (isMounted) {
          setSavingsPerKilo(String(settingResponse.savingsPerKilo ?? 2));
          setHistoryLogs(historyResponse);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
          setHistoryLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsHistoryLoading(false);
        }
      }
    }

    loadSavingsPerKilo();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleSavingsPerKiloChange(event) {
    setSavingsPerKilo(event.target.value);
    setFormError('');
    setSubmitError('');
    setSuccessMessage('');
  }

  async function handleSavingsPerKiloSubmit(event) {
    event.preventDefault();
    const nextError = validateSavingsPerKilo(savingsPerKilo);

    if (nextError) {
      setFormError(nextError);
      return;
    }

    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const response = await updateSavingsPerKiloSetting({
        savingsPerKilo: Number(savingsPerKilo),
      });
      setSavingsPerKilo(String(response.savingsPerKilo));
      if (response.historyLog) {
        setHistoryLogs((currentLogs) => [response.historyLog, ...currentLogs]);
      }
      setSuccessMessage('Savings per kilo updated successfully.');
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  return {
    savingsPerKilo,
    formError,
    historyLoadError,
    historyLogs,
    isLoading,
    isHistoryLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    handleSavingsPerKiloChange,
    handleSavingsPerKiloSubmit,
  };
}

function validateSavingsPerKilo(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 'Savings per kilo must be zero or greater.';
  }

  return '';
}

export default useSavingsPerKiloSetting;
